import { COLORS } from './constants';
import {
  MAZE_SPEC,
  type MazeBoardSpec,
  type MazeChannelSpec,
  type MazeDeflectorSpec,
  type MazeGuardArcSpec,
  type MazeRadialWallSpec,
  type MazeRampSpec,
  type MazeRingSpec,
  type MazeSlotsSpec,
} from './mazeSpec.generated';
import { DEG2RAD } from '../utils/math';

/**
 * ============================================================================
 * THE MAZE, DERIVED FROM THE SPEC
 * ============================================================================
 *
 * Nothing in this file invents geometry. Every wall the ball collides with is
 * derived from `blender/spec/maze.json` - the same file the Blender build reads
 * to draw the board. That is the whole point of the spec: before it existed, the
 * model and the collision layout were maintained separately and drifted, and the
 * drift showed up in play as a wall the player could not see.
 *
 * The rule that follows from that, and which this file must never break in
 * either direction:
 *
 *   - the collision layout may not contain geometry the board model does not
 *     draw (an invisible wall - the ball stops dead against nothing), and
 *   - the board model may not draw geometry the collision layout lacks (the ball
 *     passes through a wall it visibly hits).
 *
 * Coordinates are in the maze body's LOCAL frame, i.e. exactly as if the board
 * were at rotation 0. The whole assembly is rotated by the rigid body, never by
 * rewriting these values.
 */

/** What produced a wall box. The debug view and the collider-count tests use it. */
export type WallKind = 'ring' | 'guard' | 'radialWall' | 'deflector' | 'ramp';

/**
 * A single box-shaped piece of maze wall.
 *
 * `kind` and `id` are bookkeeping: they let a test say "the world contains
 * exactly as many boxes as the layout has" and let the top-down debug view label
 * what it draws. Neither is ever consulted when deciding anything about the
 * ball.
 */
export interface WallSegment {
  kind: WallKind;
  /** Stable id: the spec record's id, plus a suffix when it is split. */
  id: string;
  x: number;
  y: number;
  z: number;
  /** Rotation about Z that aligns the box's local X with its long axis. */
  angleRad: number;
  /** Full extent along the box's local X: the long axis. */
  length: number;
  /** Full extent along local Y: the wall thickness. */
  thickness: number;
  /** Full extent along local Z (depth, out of the board). */
  depth: number;
  color: number;
  emissiveIntensity: number;
}

/** A cylindrical peg. Rapier has a cylinder primitive, so it needs no tessellation. */
export interface ObstacleCollider {
  id: string;
  x: number;
  y: number;
  /** Centre along Z, i.e. the middle of the wall depth band - same meaning as `WallSegment.z`. */
  z: number;
  /** Peg radius. */
  radius: number;
  /** Full extent along Z. */
  height: number;
  color: number;
  emissiveIntensity: number;
}

export interface MazeLayout {
  /** Box-shaped walls: rings, guard arcs, radial walls, deflectors, ramp steps. */
  segments: WallSegment[];
  /** Cylindrical pegs. */
  obstacles: ObstacleCollider[];
  board: MazeBoardSpec;
  channels: readonly MazeChannelSpec[];
  rings: readonly MazeRingSpec[];
  guardArcs: readonly MazeGuardArcSpec[];
  radialWalls: readonly MazeRadialWallSpec[];
  deflectors: readonly MazeDeflectorSpec[];
  ramps: readonly MazeRampSpec[];
  slots: MazeSlotsSpec;
}

// ---------------------------------------------------------------------------
// tessellation
// ---------------------------------------------------------------------------

/**
 * Tessellate a circular arc into tangent box segments.
 *
 * `count` boxes are laid along the arc. Each box is a chord of the circle, so
 * its half-length uses tan() rather than sin() - that makes neighbouring boxes
 * meet at their corners instead of leaving a notch. A 2% overlap absorbs
 * floating-point error.
 */
function arcSegments(options: {
  kind: WallKind;
  id: string;
  radius: number;
  thickness: number;
  startDeg: number;
  endDeg: number;
  count: number;
  depth: number;
  color: number;
  emissiveIntensity: number;
}): WallSegment[] {
  const sweepDeg = options.endDeg - options.startDeg;
  if (sweepDeg <= 0 || options.count <= 0) return [];

  const stepDeg = sweepDeg / options.count;
  const halfLength = options.radius * Math.tan((stepDeg * DEG2RAD) / 2) * 1.02;

  const out: WallSegment[] = [];
  for (let index = 0; index < options.count; index += 1) {
    const midDeg = options.startDeg + (index + 0.5) * stepDeg;
    const midRad = midDeg * DEG2RAD;
    out.push({
      kind: options.kind,
      id: options.count === 1 ? options.id : `${options.id}#${index}`,
      x: options.radius * Math.cos(midRad),
      y: options.radius * Math.sin(midRad),
      z: options.depth === 0 ? 0 : MAZE_SPEC.board.wallZ,
      // Rotating local +X by (theta + 90 deg) points it along the arc tangent.
      angleRad: midRad + Math.PI / 2,
      length: halfLength * 2,
      thickness: options.thickness,
      depth: options.depth,
      color: options.color,
      emissiveIntensity: options.emissiveIntensity,
    });
  }
  return out;
}

/**
 * Tessellate a RADIAL wall into box segments.
 *
 * A radial wall's long axis runs along the radius, not the tangent, so it is the
 * one place where `angleRad` is the mid-angle itself rather than mid + 90. The
 * distinction matters: getting it wrong turns every radial wall into a tangential
 * one and the maze stops being a maze.
 */
function radialSegments(options: {
  kind: WallKind;
  id: string;
  centerDeg: number;
  fromRadius: number;
  toRadius: number;
  thickness: number;
  depth: number;
  color: number;
  emissiveIntensity: number;
}): WallSegment {
  const centerRad = options.centerDeg * DEG2RAD;
  const midRadius = (options.fromRadius + options.toRadius) / 2;
  return {
    kind: options.kind,
    id: options.id,
    x: midRadius * Math.cos(centerRad),
    y: midRadius * Math.sin(centerRad),
    z: MAZE_SPEC.board.wallZ,
    angleRad: centerRad,
    length: options.toRadius - options.fromRadius,
    thickness: options.thickness,
    depth: options.depth,
    color: options.color,
    emissiveIntensity: options.emissiveIntensity,
  };
}

// ---------------------------------------------------------------------------
// per-group builders
// ---------------------------------------------------------------------------

/** Presentation only. Never consulted when resolving anything. */
const RING_COLORS: Record<string, number> = {
  ring_021: COLORS.ringGold,
  ring_035: COLORS.ringYellow,
  ring_060: COLORS.ringBlue,
  ring_085: COLORS.ringRed,
};

const KIND_COLORS: Record<WallKind, number> = {
  ring: COLORS.ringBlue,
  guard: COLORS.outerRing,
  radialWall: COLORS.ringRed,
  deflector: COLORS.neonCyan,
  ramp: COLORS.neonMagenta,
};

function ringColor(ring: MazeRingSpec): number {
  return RING_COLORS[ring.id] ?? KIND_COLORS.ring;
}

/**
 * The concentric rails, one arc per solid span.
 *
 * The guard is NOT one of these. It is a separate group because it is the exit
 * rather than a rail, and because its arcs are what aim the shot.
 */
function buildRings(): WallSegment[] {
  const out: WallSegment[] = [];
  for (const ring of MAZE_SPEC.rings) {
    const depth = MAZE_SPEC.board.wallDepth;
    ring.solidSpans.forEach((span, index) => {
      const sweepDeg = span.endDeg - span.startDeg;
      const count = Math.max(1, Math.round((ring.segments * sweepDeg) / 360));
      out.push(
        ...arcSegments({
          kind: 'ring',
          id: ring.solidSpans.length === 1 ? ring.id : `${ring.id}#span${index}`,
          radius: ring.radius,
          thickness: ring.thickness,
          startDeg: span.startDeg,
          endDeg: span.endDeg,
          count,
          depth,
          color: ringColor(ring),
          emissiveIntensity: ring.emissive,
        }),
      );
    });
  }
  return out;
}

/**
 * The transparent guard: the outer boundary, and the exit.
 *
 * Its collider follows its arcs rather than a closed circle. A proxy that closed
 * the ring would be an invisible wall in the one place where it is fatal - the
 * ball would never be able to leave the board, and the player would never be able
 * to aim.
 */
function buildGuard(): WallSegment[] {
  const board = MAZE_SPEC.board;
  return MAZE_SPEC.guard.arcs.flatMap((arc) => {
    const sweepDeg = arc.endDeg - arc.startDeg;
    const count = Math.max(1, Math.round((MAZE_SPEC.guard.segments * sweepDeg) / 360));
    return arcSegments({
      kind: 'guard',
      id: arc.id,
      radius: MAZE_SPEC.guard.radius,
      thickness: board.guardTube * 2,
      startDeg: arc.startDeg,
      endDeg: arc.endDeg,
      count,
      depth: board.wallDepth,
      color: KIND_COLORS.guard,
      emissiveIntensity: 1,
    });
  });
}

/** Segmented radial walls: one box per solid band, per wall. */
function buildRadialWalls(): WallSegment[] {
  const board = MAZE_SPEC.board;
  return MAZE_SPEC.radialWalls.flatMap((wall) =>
    wall.solidBands.map((band, index) =>
      radialSegments({
        kind: 'radialWall',
        id: wall.solidBands.length === 1 ? wall.id : `${wall.id}#band${index}`,
        centerDeg: wall.centerDeg,
        fromRadius: band.fromRadius,
        toRadius: band.toRadius,
        thickness: wall.thickness,
        depth: board.wallDepth,
        color: KIND_COLORS.radialWall,
        emissiveIntensity: 1.15,
      }),
    ),
  );
}

/**
 * Deflectors: short plates protruding from one wall of a channel.
 *
 * The spec attaches each plate to a wall (`from`) and gives its length, so its
 * centre is already resolved to a radius - there is no free-floating fraction.
 * A plate standing in the middle of a channel does not deflect the ball, it
 * blocks it, and `lib/spec.py` rejects a length that would leave too little
 * clear channel beside it.
 *
 * `skewDeg` rotates the plate away from the pure radial direction, which is what
 * turns "the ball stops here" into "the ball goes somewhere else".
 */
function buildDeflectors(): WallSegment[] {
  const board = MAZE_SPEC.board;
  return MAZE_SPEC.deflectors.map((deflector) => {
    const centerRad = deflector.centerDeg * DEG2RAD;
    return {
      kind: 'deflector' as const,
      id: deflector.id,
      x: deflector.radius * Math.cos(centerRad),
      y: deflector.radius * Math.sin(centerRad),
      z: board.wallZ,
      angleRad: centerRad + deflector.skewDeg * DEG2RAD,
      length: deflector.length,
      thickness: deflector.thickness,
      depth: board.wallDepth,
      color: KIND_COLORS.deflector,
      emissiveIntensity: 1.3,
    };
  });
}

/**
 * Ramps: a DIAGONAL chute across part of a channel.
 *
 * The spec gives the diagonal as two polar points - where it starts across the
 * channel (`atFraction`) and where it ends (`atFraction + rise`), over
 * `sweepDeg`. This walks that diagonal and emits one box per step, each aligned
 * to its own chord, with a slight overlap so the chain has no notches a ball
 * could squeeze through.
 *
 * It is a diagonal and not a wedge on purpose: the board is a vertical plane and
 * the ball rolls in two dimensions, so there is no third dimension to climb. The
 * diagonal's job is to convert downhill travel into radial travel.
 */
function buildRamps(): WallSegment[] {
  const board = MAZE_SPEC.board;
  const out: WallSegment[] = [];

  for (const ramp of MAZE_SPEC.ramps) {
    const steps = Math.max(2, Math.round(ramp.sweepDeg / 6));
    const stepSweep = ramp.sweepDeg / steps;
    const stepRise = (ramp.endRadius - ramp.atRadius) / steps;

    const pointAt = (index: number) => {
      const radius = ramp.atRadius + stepRise * index;
      const rad = (ramp.startDeg + stepSweep * index) * DEG2RAD;
      return { x: radius * Math.cos(rad), y: radius * Math.sin(rad) };
    };

    for (let index = 0; index < steps; index += 1) {
      const from = pointAt(index);
      const to = pointAt(index + 1);
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const chord = Math.hypot(dx, dy);
      out.push({
        kind: 'ramp',
        id: `${ramp.id}#${index}`,
        x: (from.x + to.x) / 2,
        y: (from.y + to.y) / 2,
        z: board.wallZ,
        angleRad: Math.atan2(dy, dx),
        length: chord * 1.06,
        thickness: ramp.thickness,
        depth: board.wallDepth,
        color: KIND_COLORS.ramp,
        emissiveIntensity: 1.2,
      });
    }
  }
  return out;
}

/** Pegs. Cylinders, so no tessellation is involved. */
function buildObstacles(): ObstacleCollider[] {
  const board = MAZE_SPEC.board;
  return MAZE_SPEC.obstacles.map((obstacle) => {
    const centerRad = obstacle.centerDeg * DEG2RAD;
    return {
      id: obstacle.id,
      x: obstacle.radius * Math.cos(centerRad),
      y: obstacle.radius * Math.sin(centerRad),
      // A peg stands in the channel, so it fills the same depth band as the
      // walls around it. Its centre is the wall plane, not the plate face.
      z: board.wallZ,
      radius: obstacle.pegRadius,
      height: board.wallDepth,
      color: COLORS.scoreSilver,
      emissiveIntensity: 0.6,
    };
  });
}

// ---------------------------------------------------------------------------
// assembly
// ---------------------------------------------------------------------------

/**
 * The groups the board model draws, and therefore the groups that may have
 * colliders.
 *
 * Adding a group here without adding it to the Blender build is exactly the bug
 * this list exists to prevent, so it is deliberately a single visible switch
 * rather than something implied by the code below.
 */
const LAYOUT_GROUPS = {
  rings: true,
  guard: true,
  radialWalls: true,
  deflectors: true,
  ramps: true,
  obstacles: true,
} as const;

/**
 * There is deliberately NO scoring geometry in here.
 *
 * Nothing inside the maze scores any more. The plate used to be painted with
 * concentric neon bands matching the old five radial zones, and a set of bands
 * that no longer score would tell the player the opposite of the truth - so the
 * bands, the zones and the colliders that matched them are all gone together.
 * The only thing that scores is the row of pockets along the bottom edge of the
 * machine, and that row does not rotate with the board.
 */
export function buildMazeLayout(): MazeLayout {
  const segments: WallSegment[] = [];
  if (LAYOUT_GROUPS.rings) segments.push(...buildRings());
  if (LAYOUT_GROUPS.guard) segments.push(...buildGuard());
  if (LAYOUT_GROUPS.radialWalls) segments.push(...buildRadialWalls());
  if (LAYOUT_GROUPS.deflectors) segments.push(...buildDeflectors());
  if (LAYOUT_GROUPS.ramps) segments.push(...buildRamps());

  const obstacles = LAYOUT_GROUPS.obstacles ? buildObstacles() : [];

  return {
    segments,
    obstacles,
    board: MAZE_SPEC.board,
    channels: MAZE_SPEC.channels,
    rings: MAZE_SPEC.rings,
    guardArcs: MAZE_SPEC.guard.arcs,
    radialWalls: MAZE_SPEC.radialWalls,
    deflectors: MAZE_SPEC.deflectors,
    ramps: MAZE_SPEC.ramps,
    slots: MAZE_SPEC.slots,
  };
}
